import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { proDesignTokens } from './ProDesignTokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import { r } from '../../theme/tokens';

/**
 * Global banner for users whose complimentary (early-user) access has ended.
 *
 * Deliberately separate from TrialExpiredBanner: this cohort never had a
 * Stripe trial or payment, so the copy must acknowledge the prior benefit
 * without implying a failed payment, an ended trial, or a lapsed Membership
 * (docs/complimentary-access-expiration.md §3.5).
 */
const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: proDesignTokens.borderRadius.large,
  border: `${r(1)} solid ${proDesignTokens.colors.pro.accentLight}`,
  backgroundColor: proDesignTokens.colors.background.card,
  boxShadow: proDesignTokens.shadows.banner,
  margin: proDesignTokens.spacing.medium,
  marginBottom: proDesignTokens.spacing.large,
  '& .MuiAlert-message': {
    width: '100%',
    padding: 0
  },
  '& .MuiAlert-action': {
    padding: 0,
    marginRight: 0
  }
}));

const BannerContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: proDesignTokens.spacing.medium,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: proDesignTokens.spacing.small
  }
}));

export default function ComplimentaryExpiredBanner({ onDismiss, source = 'complimentary_expired_banner' }) {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    logAnalyticsEvent('complimentary_expired_banner_shown', { source });
  }, [source]);

  const handleStartMembership = () => {
    logAnalyticsEvent('complimentary_expired_banner_membership_clicked', { source });

    navigate('/subscribe', {
      state: {
        from: { pathname: window.location.pathname },
        source,
      },
    });
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }

    logAnalyticsEvent('complimentary_expired_banner_dismissed', { source });
  };

  if (dismissed) {
    return null;
  }

  return (
    <StyledAlert
      severity="info"
      icon={false}
      action={
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss"
          sx={{
            color: proDesignTokens.colors.neutral[500],
            '&:hover': {
              backgroundColor: proDesignTokens.colors.neutral[100]
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <BannerContent>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: proDesignTokens.colors.neutral[900],
              mb: 0.5
            }}
          >
            Your complimentary Membership has ended
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: proDesignTokens.colors.neutral[600],
              lineHeight: 1.4
            }}
          >
            Your saved stacks are still here. Start your Membership to unlock advanced features.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: proDesignTokens.spacing.small }}>
          <Button
            variant="contained"
            size="medium"
            onClick={handleStartMembership}
            sx={{
              ...proDesignTokens.components.primaryButton,
              minWidth: '8.75rem',
              height: '2.5rem'
            }}
          >
            Start Membership
          </Button>
        </Box>
      </BannerContent>
    </StyledAlert>
  );
}
